import { Component, OnInit, ViewChild, Renderer2, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from 'src/app/providers/auth-service/auth-service';
import { ProjectService } from 'src/app/providers/project-service/project-service';
import { Location } from '@angular/common';
import { PriceTier, StripeService } from 'src/app/providers/stripe-service/stripe-service';
import { AlertController, RangeCustomEvent, ToastController } from '@ionic/angular';
import { Project } from 'src/app/models/Project';
import { NotificationMessage } from 'src/app/components/notification-message/notification-message.component';
import { TranslateService } from '@ngx-translate/core';
import { TranslationBridge } from 'src/app/providers/translation-bridge/translation-bridge';

@Component({
  selector: 'app-upgrade',
  templateUrl: './upgrade.page.html',
  styleUrls: ['./upgrade.page.scss'],
})
export class UpgradePage implements OnInit {

  loading: boolean = true;
  project: any = null;

  usedBankAccounts: number = 1;
  desiredBankAccounts: number = 1;
  maxBankAccounts: number = 25;

  priceInterval: string = "monthly";
  choosenSubscription: string = "";

  priceTimeout: any = null;
  priceLoading: boolean = false;
  price: number = 0;
  priceTier: PriceTier;

  invalidConfiguration: boolean = false;
  loadingCheckout: boolean = false;
  checkout: boolean = false;

  cancelSubscriptionLoading: boolean = false;

  messages: NotificationMessage[] = [];

  constructor(
    private activatedRoute: ActivatedRoute,
    private projectService: ProjectService,
    private translate: TranslationBridge,
    private authService: AuthService,
    private location: Location,
    private stripeService: StripeService,
    private alertController: AlertController,
    private toastController: ToastController,
    ) { }

  ngOnInit() {
    this.priceLoading = true;
    this.loadProject().then((project: Project) => {
      this.usedBankAccounts = (project.usage.bankAccounts ? project.usage.bankAccounts : 1);
      this.desiredBankAccounts = this.usedBankAccounts;
      this.maxBankAccounts = (project.limits.buyableBankAccounts ? project.limits.buyableBankAccounts : 25);
      this.updatePricing();
    });
  }

  ngAfterViewInit(){

  }

  loadProject() {
    return new Promise(async (resolve, reject) => {
      let projectId = this.activatedRoute.snapshot.paramMap.get('projectId');
      let currentUrl = "/upgrade";
      this.project = this.projectService.getCurrentProject();
      this.projectService.isReady().then(() => {
        this.projectService.getLastOpenedProject().then((lastOpenedProject) => {

          if (this.project && this.project.id == projectId) { // current Project was set
            return resolve(this.project);
          }

          if (projectId) {
            this.projectService.setCurrentProject(projectId).then(() => {
              this.project = this.projectService.getCurrentProject();
              this.loading = false;
              return resolve(this.project);
            });
          } else if (!projectId) {

            if (lastOpenedProject.id) { // get the last opened project
              this.projectService.setCurrentProject(lastOpenedProject.id).then(() => {
                this.project = this.projectService.getCurrentProject();
                this.location.replaceState(this.projectService.currentProject.id + currentUrl);
                this.loading = false;
                return resolve(this.project);
              });

            } else {
              // no Project Created yet
              //this.createProject();
              this.loading = false;
              return resolve(this.project);
            }

          }

        });
      });
    });
  }

  updateMessages(){
    this.messages = [];

    if(this.desiredBankAccounts >= this.maxBankAccounts){
      this.messages.push({
        type: "primary-light",
        icon: "trend-up",
        title: "",
        text: this.translate.get("PRICING_PAGE_MESSAGE_BANK_ACCOUNTS_ENTERPRISE").replace("%COUNT%", this.maxBankAccounts.toString()),
        actionTitle: this.translate.get("PRICING_PAGE_MESSAGE_BANK_ACCOUNTS_ENTERPRISE_BUTTON"),
        action: () => {
          window.open("mailto:support@kontoflux.io", '_blank');
        }
      });
    }

    if(this.projectService?.currentProject?.isCanceled){
      this.messages.push({
        type: "warning",
        icon: "arrows-clockwise",
        title: "",
        text: this.translate.get("PRICING_PAGE_MESSAGE_IS_CANCELED"),
        actionTitle: this.translate.get("PRICING_PAGE_MESSAGE_IS_CANCELED_BUTTON_REACTIVATE"),
        action: async () => {
          let portal = await this.stripeService.getCustomerPortalLink(this.project.id);
          if(portal.url){
            window.open(portal.url, '_blank');
          }
        }
      });
    }

    if(this.projectService?.currentProject?.isSubscribed){


      if(this.desiredBankAccounts < this.usedBankAccounts){
        this.messages.push({
          type: "warning",
          icon: "bank",
          title: "",
          text: this.translate.get("PRICING_PAGE_MESSAGE_BANK_ACCOUNTS_DECREASED"),
          actionTitle: "",
          action: null
        });
      }
      
      if(this.messages.length == 0){
        this.messages.push({
          type: "primary-light",
          icon: "info",
          title: "",
          text: this.translate.get("PRICING_PAGE_MESSAGE_UPGRADE_POSSIBLE"),
          actionTitle: "",
          action: null
        });
      }

    } else {
      if(this.desiredBankAccounts < this.usedBankAccounts){
        this.messages.push({
          type: "warning",
          icon: "bank",
          title: "",
          text: this.translate.get("PRICING_PAGE_MESSAGE_BANK_ACCOUNTS_TO_LOW"),
          actionTitle: "",
          action: null
        });
      }

      if(!this.isInPast(new Date("2024-05-31T23:59:59.999Z"))){
        this.messages.push({
          type: "primary-light",
          icon: "gift",
          title: "",
          text: this.translate.get("PRICING_PAGE_MESSAGE_BETA_COUPON"),
          actionTitle: "",
          action: null
        });

      }

    }


  }

  selectBankAccountChange(event: any){
    this.desiredBankAccounts = parseInt(event.detail.value);
    if(this.priceTimeout) clearTimeout(this.priceTimeout);
    this.priceLoading = true;
    this.priceTimeout = setTimeout(() => {
      this.updatePricing();
    }, 500);
  }

  setPeriodMonthly(){
    this.priceInterval = "monthly";
    this.updatePricing();
  }
  setPeriodYearly(){
    this.priceInterval = "yearly";
    this.updatePricing();
  }

  async checkoutProduct(product: string){
    try {
      this.messages = [];
      this.loadingCheckout = true;
      this.checkout = true;

      // wait a sec for rendering purposes
      await new Promise((resolve, reject) => {
        setTimeout(() => {
          resolve(true);
        }, 100);
      });

      if(this.projectService.currentProject.isCanceled){
        let portalResponse = await this.stripeService.getCustomerPortalLink(this.project.id,'portal');
        this.loadingCheckout = false;
        if(portalResponse.url){
          window.open(portalResponse.url, '_blank');
          this.checkout = false;
          this.messages = [{
            type: "primary-light",
            icon: "info",
            title: "",
            text: this.translate.get("PRICING_PAGE_MESSAGE_UPGRADE_CHANGE_SUBSCRIPTION_IN_WINDOW"),
            actionTitle: "",
            action: null
          }];
        } else {
          throw new Error("Cannot open the customer portal. No URL.");
        }
        return;
      }

      if(!this.projectService.currentProject.isSubscribed){
        await this.stripeService.checkoutProduct(product, this.desiredBankAccounts, this.project.id, document.getElementById('stripe-checkout'));
        this.loadingCheckout = false;
      } else {
        let portalResponse = await this.stripeService.changeProduct(product, this.desiredBankAccounts, this.project.id);
        this.loadingCheckout = false;
        if(portalResponse.url){
          window.open(portalResponse.url, '_blank');
          this.checkout = false;
          this.messages = [{
            type: "primary-light",
            icon: "info",
            title: "",
            text: this.translate.get("PRICING_PAGE_MESSAGE_UPGRADE_CHANGE_SUBSCRIPTION_IN_WINDOW"),
            actionTitle: "",
            action: null
          }];
        } else {
          throw new Error("Cannot open the customer portal. No URL.");
        }

      }

    } catch(e){
      this.loadingCheckout = false;
      this.toastController.create({
        message: (e.error ? e.error.message : e.message),
        duration: 5000,
        position: 'top',
        cssClass: 'toast-error'
      }).then((toast) => {
        toast.present();
      });
    }
  }

  cancelCheckout(){
    this.stripeService.cancelCheckoutSession();
    this.checkout = false;
    this.loadingCheckout = false;
  }

  async updatePricing(){
    this.priceLoading = true;
    let priceTier = await this.stripeService.getPriceTiers(this.desiredBankAccounts, this.priceInterval);

    this.price = priceTier.price;
    this.choosenSubscription = priceTier.priceKey;
    this.priceTier = priceTier;

    this.priceLoading = false;
    this.invalidConfiguration = false;

    // for already subscribed user prevent to subscribe the same product again
    if(this.projectService?.currentProject?.isSubscribed){
      if(priceTier.tier == this.projectService.currentProject?.subscription){
        if(priceTier.tier == "pro"){
          this.invalidConfiguration = true;
        } else {
          if(this.projectService.currentProject.limits.bankAccounts == this.desiredBankAccounts && this.priceInterval == this.projectService.currentProject.subscriptionInterval) this.invalidConfiguration = true;
        }
      }
    }

    this.updateMessages();

  }

  async cancelSubscription(){
    let alert = await this.alertController.create({
      header: this.translate.get('UPGRADE_CANCEL_SUBSCRIPTION_TITLE'),
      message: this.translate.get('UPGRADE_CANCEL_SUBSCRIPTION_INFO'),
      buttons: [
        {
          text: this.translate.get('CANCEL'),
          role: 'cancel',
          handler: () => {
          },
        },
        {
          text: this.translate.get('UPGRADE_CANCEL_SUBSCRIPTION_BUTTON'),
          role: 'descructive',
          handler: async (data: any) => {
         
              this.cancelSubscriptionLoading = true;
              this.stripeService.getCustomerPortalLink(this.project.id,'cancel-subscription').then((result) => {
                window.open(result.url, '_blank');
                this.cancelSubscriptionLoading = false;
              }).catch((error) => {
                this.cancelSubscriptionLoading = false;
                this.toastController.create({
                  message: (error.error.message ? error.error.message : error),
                  duration: 5000,
                  position: 'top',
                  cssClass: 'toast-error',
                  buttons: [
                    {
                      text: 'OK',
                      role: 'cancel',
                      handler: () => {
                      }
                    }
                  ]
                }).then((toast) => {
                  toast.present();
                });
              });

          }
        }
      ],
    });
    await alert.present();
  }

  /*
    date is in past
  */
  public isInPast(date: Date){
    return (new Date(date).getTime() < new Date().getTime());
  }
  

}
